/**
 * `Composer` — the visitor's message input (ARCHITECTURE §7). Controlled text
 * field + send button; Enter sends, Shift+Enter newlines. Includes an emoji picker
 * (inserted at the caret).
 */

import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { EmojiPicker } from './EmojiPicker';

export interface ComposerProps {
  /** Called with the trimmed message body when the visitor sends. */
  onSend: (body: string) => void;
  /** Upload + send a file attachment (image/PDF). */
  onAttach?: (file: File) => void | Promise<void>;
  /** Fired on every keystroke — drives the typing indicator on the agent side. */
  onTyping?: () => void;
  disabled?: boolean;
  placeholder?: string;
}

export function Composer({
  onSend,
  onAttach,
  onTyping,
  disabled = false,
  placeholder = 'Type a message…',
}: ComposerProps) {
  const [value, setValue] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const submit = (): void => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue('');
  };

  /** Insert an emoji at the caret (or append), restoring focus + caret after it. */
  const insertEmoji = (emoji: string): void => {
    const ta = taRef.current;
    if (!ta) {
      setValue((v) => v + emoji);
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const next = value.slice(0, start) + emoji + value.slice(end);
    setValue(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + emoji.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    submit();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-1.5 border-t border-black/10 p-2">
      {onAttach ? (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              e.currentTarget.value = '';
              if (file && !disabled) void onAttach(file);
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={disabled}
            aria-label="Attach a file"
            title="Attach a file"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-lc-text/60 transition-colors hover:bg-black/5 hover:text-lc-text disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden>
              <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
        </>
      ) : null}
      <EmojiPicker onPick={insertEmoji} disabled={disabled} />
      <textarea
        ref={taRef}
        className="max-h-24 min-h-[2.5rem] flex-1 resize-none rounded-lc bg-black/5 px-3 py-2 text-sm text-lc-text outline-none focus:ring-2 focus:ring-lc-primary/40"
        rows={1}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          setValue(e.target.value);
          onTyping?.();
        }}
        onKeyDown={handleKeyDown}
      />
      <button
        type="submit"
        disabled={disabled || value.trim().length === 0}
        aria-label="Send message"
        title="Send"
        style={{ background: 'var(--lc-send-btn)' }}
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-lc-on-primary transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {/* Paper-plane icon (parity with the dashboard composer — icons, not text). */}
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden>
          <path d="M3.4 20.4l17.45-7.48a1 1 0 0 0 0-1.84L3.4 3.6a1 1 0 0 0-1.4.92V9.6a1 1 0 0 0 .82.98L14 12 2.82 13.42a1 1 0 0 0-.82.98v5.08a1 1 0 0 0 1.4.92z" />
        </svg>
      </button>
    </form>
  );
}
